import React, { useEffect, useState } from "react";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  makeStyles,
  Typography,
  Box,
  Chip,
  Tooltip,
  Button,
  Card,
  CardContent,
  Divider,
  Select,
  Dialog,
  DialogContent,
} from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import ConfirmationModal from "../ConfirmationModal";
import RegisterCompanyImproved from "./RegisterCompanyImproved";

import {
  Edit as EditIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Public as PublicIcon,
} from "@material-ui/icons";

import { head, isArray } from "lodash";
import { toast } from "react-toastify";
import useCompanies from "../../hooks/useCompanies";
import { useDate } from "../../hooks/useDate";
import { useTimezone } from "../../hooks/useTimezone";
import usePlans from "../../hooks/usePlans";
import api from "../../services/api";
import ModalUsers from "../ModalUsers";
import { i18n } from "../../translate/i18n";
import moment from "moment";
import * as Yup from "yup";
import {
  useViewMode,
  ViewModeToggle,
  RecordsCardsGrid,
  RecordCard,
  RecordCardMeta,
} from "../ViewModeToggle";

const EMPTY_COMPANY_RECORD = {
  name: "",
  email: "",
  phone: "",
  planId: "",
  status: "true",
  campaignsEnabled: "false",
  dueDate: "",
  recurrence: "",
  timezone: "",
};

const companySchema = Yup.object().shape({
  name: Yup.string().trim().min(2, "El nombre es obligatorio").required("El nombre es obligatorio"),
  email: Yup.string().trim().email("Correo electrónico inválido").required("El correo es obligatorio"),
  phone: Yup.string().trim().nullable(),
  planId: Yup.string().required("Selecciona un plan"),
  dueDate: Yup.string()
    .nullable()
    .test("valid-date", "Fecha de vencimiento inválida", (value) => {
      if (!value) return true;
      return moment(value, "YYYY-MM-DD", true).isValid();
    }),
  timezone: Yup.string().nullable(),
  recurrence: Yup.string().nullable(),
});

const toBoolFlag = (value, fallback = true) => {
  if (value === true || value === "true" || value === 1 || value === "1") return true;
  if (value === false || value === "false" || value === 0 || value === "0") return false;
  return fallback;
};

const toFlagString = (value, fallback = true) => (toBoolFlag(value, fallback) ? "true" : "false");

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: theme.spacing(1),
  },
  // ✅ CORREGIDO: FormCard dinámico según tema
  formCard: {
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
    boxShadow: theme.mode === "dark" ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#e0e0e0"}`,
  },
  modalPaper: {
    width: "min(960px, calc(100vw - 24px))",
    maxHeight: "calc(100dvh - 24px)",
    margin: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("xs")]: {
      width: "calc(100vw - 12px)",
      maxHeight: "calc(100dvh - 12px)",
      margin: theme.spacing(0.75),
    },
  },
  modalContent: {
    padding: 0,
    overflowY: "auto",
    overflowX: "hidden",
    flex: "1 1 auto",
    minHeight: 0,
    backgroundColor: theme.palette.background.paper,
    WebkitOverflowScrolling: "touch",
  },
  modalFormCard: {
    marginBottom: 0,
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
  },
  formCardContent: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    },
  },
  formTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  formGrid: {
    marginTop: theme.spacing(2),
  },
  fieldContainer: {
    marginBottom: theme.spacing(2),
  },
  // ✅ CORREGIDO: TextField dinámico según tema
  textField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === "dark" ? "#374151" : theme.palette.background.paper,
      borderRadius: theme.spacing(1),
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: theme.mode === "dark" ? "#4B5563" : "#e0e0e0",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&:hover": {
        backgroundColor: theme.mode === "dark" ? "#4B5563" : "#fafafa",
      },
      "&.Mui-focused": {
        backgroundColor: theme.mode === "dark" ? "#374151" : theme.palette.background.paper,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      fontWeight: "500",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
  // ✅ CORREGIDO: SelectField dinámico según tema
  selectField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === "dark" ? "#374151" : theme.palette.background.paper,
      borderRadius: theme.spacing(1),
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: theme.mode === "dark" ? "#4B5563" : "#e0e0e0",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      fontWeight: "500",
    },
    "& .MuiMenuItem-root": {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      "&:hover": {
        backgroundColor: theme.mode === "dark" ? "#4B5563" : "#f5f5f5",
      },
    },
  },
  buttonContainer: {
    display: "flex",
    gap: theme.spacing(2),
    justifyContent: "flex-end",
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(-3),
    marginRight: theme.spacing(-3),
    marginBottom: theme.spacing(-3),
    padding: theme.spacing(2, 3),
    flexWrap: "wrap",
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#e0e0e0"}`,
    [theme.breakpoints.down("xs")]: {
      marginLeft: theme.spacing(-2),
      marginRight: theme.spacing(-2),
      marginBottom: theme.spacing(-2),
      padding: theme.spacing(1.5, 2),
      justifyContent: "stretch",
      "& > button": {
        flex: "1 1 100%",
      },
    },
  },
  actionButton: {
    borderRadius: theme.spacing(1),
    textTransform: "none",
    fontWeight: "600",
    padding: theme.spacing(1, 3),
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    "&:hover": {
      boxShadow: "0 2px 6px rgba(0,0,0,0.16)",
    },
    transition: "all 0.2s ease",
  },
  primaryButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  secondaryButton: {
    backgroundColor: theme.palette.error.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  clearButton: {
    backgroundColor: theme.palette.grey[500],
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.grey[600],
    },
  },
  // ✅ CORREGIDO: TableCard dinámico según tema
  tableCard: {
    borderRadius: theme.spacing(1),
    boxShadow: theme.mode === "dark" ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.1)",
    overflowX: "auto",
    overflowY: "hidden",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#e0e0e0"}`,
  },
  table: {
    minWidth: 720,
    tableLayout: "auto",
    "& th, & td": {
      whiteSpace: "nowrap",
    },
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "& .MuiTableCell-head": {
      color: "white",
      fontWeight: 700,
      fontSize: "0.75rem",
      letterSpacing: 0.3,
      borderBottom: "none",
      padding: "10px 12px",
      whiteSpace: "nowrap",
    },
  },
  tableCell: {
    borderBottom: `1px solid ${theme.mode === "dark" ? "#4B5563" : "rgba(224, 224, 224, 0.5)"}`,
    padding: "10px 12px",
    color: theme.palette.text.primary,
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  // ✅ CORREGIDO: TableRow dinámico según tema
  tableRow: {
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "#374151" : theme.palette.action.hover,
      cursor: "pointer",
    },
    transition: "background-color 0.2s ease",
  },
  editButton: {
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor:
        theme.mode === "dark" ? "rgba(59, 130, 246, 0.2)" : "rgba(102, 126, 234, 0.1)",
    },
  },
  statusChip: {
    fontWeight: "600",
    borderRadius: theme.spacing(1),
  },
  statusActive: {
    backgroundColor: "#4CAF50",
    color: "white",
  },
  statusInactive: {
    backgroundColor: "#f44336",
    color: "white",
  },
  campaignsEnabled: {
    backgroundColor: "#2196F3",
    color: "white",
  },
  campaignsDisabled: {
    backgroundColor: "#757575",
    color: "white",
  },
  planChip: {
    backgroundColor: "#FF9800",
    color: "white",
    fontWeight: "600",
  },
  // ✅ CORREGIDO: WarningRow dinámico según tema
  warningRow: {
    backgroundColor: theme.mode === "dark" ? "#78350f" : "#fff3cd",
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "#92400e" : "#ffeaa7",
    },
  },
  // ✅ CORREGIDO: DangerRow dinámico según tema
  dangerRow: {
    backgroundColor: theme.mode === "dark" ? "#7f1d1d" : "#f8d7da",
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "#991b1b" : "#f5c6cb",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

export function CompanyFormImproved(props) {
  const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
  const classes = useStyles();
  const selectMenuProps = {
    getContentAnchorEl: null,
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    PaperProps: {
      style: {
        maxHeight: 320,
      },
    },
  };
  const [plans, setPlans] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [firstUser, setFirstUser] = useState({});
  const [timezones, setTimezones] = useState([]); // ✅ DB-FIRST: Lista dinámica de timezones
  const [loadingTimezones, setLoadingTimezones] = useState(true);

  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: "true",
    campaignsEnabled: "false",
    dueDate: "",
    recurrence: "",
    timezone: "",
    ...initialValue,
    status: toFlagString(initialValue?.status, true),
    campaignsEnabled: toFlagString(initialValue?.campaignsEnabled, false),
  });

  const { list: listPlans } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const list = await listPlans();
      setPlans(list);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ DB-FIRST: Cargar timezones dinámicamente desde la base de datos
  useEffect(() => {
    const loadTimezones = async () => {
      try {
        setLoadingTimezones(true);
        const { data } = await api.get("/settings/timezones");
        if (data && data.success && data.data) {
          setTimezones(data.data);

          // ✅ CORRECCIÓN: Establecer timezone solo después de cargar timezones
          // Si el record tiene timezone vacío, usar el default
          if (!record.timezone || record.timezone === "") {
            const defaultTimezone = "America/Lima";
            // Verificar que el timezone existe en la lista cargada
            const exists = data.data.some((tz) => tz.timezone === defaultTimezone);
            if (exists) {
              setRecord((prev) => ({ ...prev, timezone: defaultTimezone }));
            }
          }
        } else {
          // Fallback: Si falla, usar lista hardcodeada mínima
          console.warn("No se pudieron cargar timezones desde BD, usando fallback");
          setTimezones([
            { timezone: "America/Lima", displayName: "Perú (America/Lima)", region: "America" },
          ]);
          if (!record.timezone || record.timezone === "") {
            setRecord((prev) => ({ ...prev, timezone: "America/Lima" }));
          }
        }
      } catch (error) {
        console.error("Error cargando timezones:", error);
        // Fallback: Lista mínima
        setTimezones([
          { timezone: "America/Lima", displayName: "Perú (America/Lima)", region: "America" },
        ]);
        if (!record.timezone || record.timezone === "") {
          setRecord((prev) => ({ ...prev, timezone: "America/Lima" }));
        }
      } finally {
        setLoadingTimezones(false);
      }
    };

    loadTimezones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadCompanyData = async () => {
      const newRecord = { ...record, ...initialValue };

      // ✅ FORMATEAR FECHA DE VENCIMIENTO SI EXISTE Y ES VÁLIDA
      if (initialValue.dueDate) {
        const momentDate = moment(initialValue.dueDate);
        if (momentDate.isValid()) {
          newRecord.dueDate = momentDate.format("YYYY-MM-DD");
        }
      }

      // ✅ CARGAR TIMEZONE DESDE SETTINGS (si estamos editando una empresa existente)
      // ✅ PRIORIDAD: Settings.timezone > Companies.timezone > Default
      // ✅ CORRECCIÓN: Asegurar que siempre se establezca un timezone válido
      if (initialValue.id) {
        try {
          const { data } = await api.get(`/settings/timezone`, {
            params: { companyId: initialValue.id },
          });
          if (data && data.value) {
            newRecord.timezone = data.value;
            console.log(`[CompanyForm] Timezone cargado desde Settings: ${data.value}`);
          } else {
            // ✅ FALLBACK 1: Si Settings no tiene valor, usar Companies.timezone
            if (initialValue.timezone) {
              newRecord.timezone = initialValue.timezone;
              console.log(
                `[CompanyForm] Timezone cargado desde Companies (Settings vacío): ${initialValue.timezone}`
              );
            } else {
              // Si tampoco hay en Companies, usar default
              newRecord.timezone = "America/Lima";
              console.log(`[CompanyForm] Timezone no encontrado, usando default: America/Lima`);
            }
          }
        } catch (error) {
          // ✅ FALLBACK 2: Si Settings falla (404, etc), usar Companies.timezone
          // ✅ 404 es esperado si Settings no tiene timezone configurado aún
          if (initialValue.timezone) {
            newRecord.timezone = initialValue.timezone;
            console.log(
              `[CompanyForm] Timezone cargado desde Companies (Settings no configurado): ${initialValue.timezone}`
            );
          } else {
            // Si tampoco hay en Companies, usar default
            newRecord.timezone = "America/Lima";
            if (error.response?.status !== 404) {
              // Solo mostrar warning si NO es un 404 (404 es esperado)
              console.warn(
                `[CompanyForm] No se pudo cargar timezone desde Settings ni Companies, usando default. Error:`,
                error.message || error
              );
            }
          }
        }
      } else if (initialValue.timezone) {
        // ✅ Si es nueva empresa pero tiene timezone en initialValue, usarlo
        newRecord.timezone = initialValue.timezone;
        console.log(
          `[CompanyForm] Timezone establecido desde initialValue: ${initialValue.timezone}`
        );
      } else {
        // ✅ Si no hay timezone en ningún lado, usar default
        newRecord.timezone = "America/Lima";
      }

      // ✅ Asegurar que planId sea string para evitar warning de Material-UI
      if (newRecord.planId && typeof newRecord.planId !== "string") {
        newRecord.planId = String(newRecord.planId);
      }

      // ✅ Si planId no existe en la lista de planes disponibles, limpiarlo
      if (newRecord.planId && plans.length > 0) {
        const planExists = plans.some((p) => String(p.id) === String(newRecord.planId));
        if (!planExists) {
          console.warn(
            `⚠️ Plan ${newRecord.planId} no existe en la lista de planes disponibles, limpiando...`
          );
          newRecord.planId = "";
        }
      }

      setRecord(newRecord);
    };

    loadCompanyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, plans]);

  // ✅ OPTIMIZADO: Recargar timezone cuando cambia record.id o cuando se detecta actualización
  // ✅ NOTA: Este useEffect es complementario a handleSubmit, que ya maneja la recarga principal
  useEffect(() => {
    const reloadTimezone = async () => {
      // Solo recargar si estamos editando una empresa existente (no nueva)
      if (record.id && record.id === initialValue.id && initialValue.id) {
        try {
          const { data } = await api.get(`/settings/timezone`, {
            params: { companyId: record.id },
          });
          if (data && data.value && data.value !== record.timezone) {
            console.log(
              `🔄 [CompanyForm] Recargando timezone desde Settings (useEffect): ${data.value}`
            );
            setRecord((prev) => ({
              ...prev,
              timezone: data.value,
            }));
          }
        } catch (error) {
          // Silencioso, no mostrar error (puede ser 404 si no existe el setting aún)
        }
      }
    };

    // Solo ejecutar si hay un ID válido
    if (record.id) {
      reloadTimezone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.id, initialValue.id]);

  const handleSubmit = async (data) => {
    const payload = { ...data };
    if (payload.dueDate === "" || moment(payload.dueDate).isValid() === false) {
      payload.dueDate = null;
    }
    payload.status = toBoolFlag(payload.status, true);
    payload.campaignsEnabled = toBoolFlag(payload.campaignsEnabled, false);
    if (payload.planId !== undefined && payload.planId !== "") {
      payload.planId = Number(payload.planId);
    }

    await onSubmit(payload);
    setRecord((prev) => ({
      ...prev,
      ...payload,
      dueDate:
        payload.dueDate != null && payload.dueDate !== ""
          ? moment(payload.dueDate).format("YYYY-MM-DD")
          : "",
      planId: payload.planId != null && payload.planId !== "" ? String(payload.planId) : "",
      status: toFlagString(payload.status, true),
      campaignsEnabled: toFlagString(payload.campaignsEnabled, false),
    }));
  };

  const handleOpenModalUsers = async () => {
    try {
      const { data } = await api.get("/users/list", {
        params: {
          companyId: initialValue.id,
        },
      });
      if (isArray(data) && data.length) {
        setFirstUser(head(data));
      }
      setModalUser(true);
    } catch (e) {
      toast.error(e);
    }
  };

  const handleCloseModalUsers = () => {
    setFirstUser({});
    setModalUser(false);
  };

  const incrementDueDate = (values, setFieldValue) => {
    const data = { ...values };
    if (data.dueDate !== "" && data.dueDate !== null) {
      switch (data.recurrence) {
        case "MENSAL":
          data.dueDate = moment(data.dueDate).add(1, "month").format("YYYY-MM-DD");
          break;
        case "BIMESTRAL":
          data.dueDate = moment(data.dueDate).add(2, "month").format("YYYY-MM-DD");
          break;
        case "TRIMESTRAL":
          data.dueDate = moment(data.dueDate).add(3, "month").format("YYYY-MM-DD");
          break;
        case "SEMESTRAL":
          data.dueDate = moment(data.dueDate).add(6, "month").format("YYYY-MM-DD");
          break;
        case "ANUAL":
          data.dueDate = moment(data.dueDate).add(12, "month").format("YYYY-MM-DD");
          break;
        default:
          break;
      }
    }
    setFieldValue("dueDate", data.dueDate || "");
    setRecord((prev) => ({
      ...prev,
      ...data,
      dueDate: data.dueDate || "",
    }));
  };

  return (
    <>
      <ModalUsers
        userId={firstUser.id}
        companyId={initialValue.id}
        open={modalUser}
        onClose={handleCloseModalUsers}
      />

      <Card className={`${classes.formCard} ${classes.modalFormCard}`}>
        <CardContent className={classes.formCardContent}>
          <Typography variant="h5" className={classes.formTitle}>
            <BusinessIcon />
            {record.id
              ? i18n.t("companies.formTitle.edit")
              : i18n.t("companies.formTitle.registerNew")}
          </Typography>

          <Formik
            enableReinitialize
            initialValues={record}
            validationSchema={companySchema}
            key={`form-${record.id || "new"}-${record.dueDate || "no-date"}-${record.timezone || "no-timezone"}`}
            onSubmit={async (values) => {
              await handleSubmit(values);
            }}
          >
            {({ values, setFieldValue, errors, touched }) => {
              // ✅ Validar que planId existe en la lista de planes disponibles
              let validPlanId = values.planId || "";
              if (validPlanId && plans.length > 0) {
                const planExists = plans.some((p) => String(p.id) === String(validPlanId));
                if (!planExists) {
                  // Si el plan no existe, limpiar el valor para evitar warning
                  validPlanId = "";
                  // Actualizar el valor en Formik si es diferente
                  if (values.planId !== validPlanId) {
                    setFieldValue("planId", validPlanId);
                  }
                }
              } else if (validPlanId && plans.length === 0) {
                // Si planes aún no se han cargado, limpiar temporalmente
                validPlanId = "";
              }

              // ✅ Validar que timezone existe en la lista de timezones disponibles
              // ✅ CORRECCIÓN: Mantener el valor actual aunque los timezones no se hayan cargado
              let validTimezone = values.timezone || "";

              // Solo validar si los timezones ya se cargaron
              if (validTimezone && !loadingTimezones && timezones.length > 0) {
                const timezoneExists = timezones.some((tz) => tz.timezone === validTimezone);
                if (!timezoneExists) {
                  // Si el timezone no existe en la lista, usar el default
                  console.warn(
                    `⚠️ Timezone "${validTimezone}" no existe en la lista, usando default`
                  );
                  validTimezone = "America/Lima";
                  // Verificar que el default existe
                  const defaultExists = timezones.some((tz) => tz.timezone === validTimezone);
                  if (!defaultExists && timezones.length > 0) {
                    // Si el default no existe, usar el primero disponible
                    validTimezone = timezones[0].timezone;
                  }
                  // Actualizar el valor en Formik si es diferente
                  if (values.timezone !== validTimezone) {
                    setFieldValue("timezone", validTimezone);
                  }
                }
              }
              // ✅ NO limpiar el timezone si aún se están cargando - mantener el valor actual
              // Esto permite que el dropdown muestre el valor correcto aunque los timezones no se hayan cargado

              return (
                <Form>
                  <Grid container spacing={3} className={classes.formGrid}>
                    {/* Información Básica */}
                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.sectionTitle}>
                        <BusinessIcon />
                        {i18n.t("companies.sections.basicInfo")}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <div className={classes.fieldContainer}>
                        <TextField
                          label={i18n.t("companies.form.name")}
                          name="name"
                          value={values.name || ""}
                          onChange={(e) => setFieldValue("name", e.target.value)}
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          variant="outlined"
                          fullWidth
                          className={classes.textField}
                          InputProps={{
                            startAdornment: (
                              <BusinessIcon style={{ marginRight: 8, color: "#666" }} />
                            ),
                          }}
                        />
                      </div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <div className={classes.fieldContainer}>
                        <TextField
                          label={i18n.t("companies.form.email")}
                          name="email"
                          value={values.email || ""}
                          onChange={(e) => setFieldValue("email", e.target.value)}
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                          variant="outlined"
                          fullWidth
                          required
                          className={classes.textField}
                          InputProps={{
                            startAdornment: <EmailIcon style={{ marginRight: 8, color: "#666" }} />,
                          }}
                        />
                      </div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <div className={classes.fieldContainer}>
                        <TextField
                          label={i18n.t("companies.form.phone")}
                          name="phone"
                          value={values.phone || ""}
                          onChange={(e) => setFieldValue("phone", e.target.value)}
                          variant="outlined"
                          fullWidth
                          className={classes.textField}
                          InputProps={{
                            startAdornment: <PhoneIcon style={{ marginRight: 8, color: "#666" }} />,
                          }}
                        />
                      </div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <div className={classes.fieldContainer}>
                        <FormControl variant="outlined" fullWidth className={classes.selectField}>
                          <InputLabel>{i18n.t("companies.form.plan")}</InputLabel>
                          <Field
                            as={Select}
                            name="planId"
                            label={i18n.t("companies.form.plan")}
                            required
                            value={validPlanId}
                            error={touched.planId && Boolean(errors.planId)}
                            MenuProps={selectMenuProps}
                          >
                            {plans && plans.length > 0 ? (
                              plans.map((plan, key) => (
                                <MenuItem key={key} value={String(plan.id)}>
                                  <StarIcon style={{ marginRight: 8 }} />
                                  {plan.name}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem value="" disabled>
                                {i18n.t("companies.loadingPlans")}
                              </MenuItem>
                            )}
                          </Field>
                        </FormControl>
                      </div>
                    </Grid>

                    {/* Configuración */}
                    <Grid item xs={12}>
                      <Divider style={{ margin: "20px 0" }} />
                      <Typography variant="h6" className={classes.sectionTitle}>
                        <SettingsIcon />
                        {i18n.t("companies.sections.configuration")}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <div className={classes.fieldContainer}>
                        <FormControl variant="outlined" fullWidth className={classes.selectField}>
                          <InputLabel>{i18n.t("companies.form.status")}</InputLabel>
                          <Field
                            as={Select}
                            name="status"
                            label={i18n.t("companies.form.status")}
                            MenuProps={selectMenuProps}
                          >
                            <MenuItem value="true">
                              <CheckCircleIcon style={{ marginRight: 8, color: "#4CAF50" }} />
                              {i18n.t("companies.values.yes")}
                            </MenuItem>
                            <MenuItem value="false">
                              <CancelIcon style={{ marginRight: 8, color: "#f44336" }} />
                              {i18n.t("companies.values.no")}
                            </MenuItem>
                          </Field>
                        </FormControl>
                      </div>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <div className={classes.fieldContainer}>
                        <Field
                          component={TextField}
                          label={i18n.t("companies.form.dueDate")}
                          type="date"
                          name="dueDate"
                          variant="outlined"
                          fullWidth
                          className={classes.textField}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            startAdornment: (
                              <CalendarIcon style={{ marginRight: 8, color: "#666" }} />
                            ),
                          }}
                          onChange={(e) => setFieldValue("dueDate", e.target.value)}
                          value={values.dueDate || ""}
                        />
                      </div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <div className={classes.fieldContainer}>
                        <FormControl variant="outlined" fullWidth className={classes.selectField}>
                          <InputLabel>{i18n.t("companies.form.recurrence")}</InputLabel>
                          <Field
                            as={Select}
                            name="recurrence"
                            label={i18n.t("companies.form.recurrence")}
                            MenuProps={selectMenuProps}
                          >
                            <MenuItem value="MENSAL">{i18n.t("companies.values.monthly")}</MenuItem>
                            <MenuItem value="BIMESTRAL">
                              {i18n.t("companies.values.bimonthly")}
                            </MenuItem>
                            <MenuItem value="TRIMESTRAL">
                              {i18n.t("companies.values.quarterly")}
                            </MenuItem>
                            <MenuItem value="SEMESTRAL">
                              {i18n.t("companies.values.semiannual")}
                            </MenuItem>
                            <MenuItem value="ANUAL">{i18n.t("companies.values.annual")}</MenuItem>
                          </Field>
                        </FormControl>
                      </div>
                    </Grid>

                    {/* ✅ Zona Horaria de la Empresa */}
                    <Grid item xs={12} md={6}>
                      <div className={classes.fieldContainer}>
                        <FormControl variant="outlined" fullWidth className={classes.selectField}>
                          <InputLabel>
                            <PublicIcon
                              fontSize="small"
                              style={{ marginRight: 6, verticalAlign: "middle" }}
                            />
                            {i18n.t("companies.timezone.label")}
                          </InputLabel>
                          <Field
                            as={Select}
                            name="timezone"
                            label={i18n.t("companies.timezone.label")}
                            disabled={loadingTimezones}
                            value={validTimezone || ""}
                            MenuProps={selectMenuProps}
                          >
                            {loadingTimezones ? (
                              <>
                                <MenuItem value="" disabled>
                                  {i18n.t("companies.timezone.loading")}
                                </MenuItem>
                                {/* ✅ Mostrar el valor actual aunque se estén cargando */}
                                {validTimezone && (
                                  <MenuItem value={validTimezone} disabled>
                                    {i18n.t("companies.timezone.loadingValue", {
                                      tz: validTimezone,
                                    })}
                                  </MenuItem>
                                )}
                              </>
                            ) : timezones.length > 0 ? (
                              // ✅ DB-FIRST: Renderizar dinámicamente desde la base de datos
                              timezones.map((tz) => (
                                <MenuItem key={tz.id || tz.timezone} value={tz.timezone}>
                                  {tz.displayName}
                                </MenuItem>
                              ))
                            ) : (
                              // ✅ Fallback: Lista mínima si no hay datos + mostrar valor actual si existe
                              <>
                                {validTimezone &&
                                  !timezones.some((tz) => tz.timezone === validTimezone) && (
                                    <MenuItem value={validTimezone}>
                                      {i18n.t("companies.timezone.fallbackCurrent", {
                                        tz: validTimezone,
                                      })}
                                    </MenuItem>
                                  )}
                                <MenuItem value="America/Lima">Perú (America/Lima)</MenuItem>
                                <MenuItem value="America/Bogota">
                                  Colombia (America/Bogota)
                                </MenuItem>
                                <MenuItem value="America/Mexico_City">
                                  México (America/Mexico_City)
                                </MenuItem>
                                <MenuItem value="America/Argentina/Buenos_Aires">
                                  Argentina (America/Argentina/Buenos_Aires)
                                </MenuItem>
                                <MenuItem value="America/Santiago">
                                  Chile (America/Santiago)
                                </MenuItem>
                              </>
                            )}
                          </Field>
                        </FormControl>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{ marginTop: 4, display: "block" }}
                        >
                          {i18n.t("companies.timezone.hint")}
                        </Typography>
                      </div>
                    </Grid>
                  </Grid>

                  {/* Botones de Acción */}
                  <div className={classes.buttonContainer}>
                    <Button
                      variant="contained"
                      className={`${classes.actionButton} ${classes.clearButton}`}
                      onClick={() => onCancel()}
                      startIcon={<ClearIcon />}
                      disabled={loading}
                    >
                      {i18n.t("companies.buttons.clear")}
                    </Button>

                    {record.id !== undefined ? (
                      <>
                        <Button
                          variant="contained"
                          className={`${classes.actionButton} ${classes.secondaryButton}`}
                          onClick={() => onDelete(record)}
                          startIcon={<DeleteIcon />}
                          disabled={loading}
                        >
                          {i18n.t("companies.buttons.delete")}
                        </Button>

                        <Button
                          variant="contained"
                          className={`${classes.actionButton} ${classes.primaryButton}`}
                          onClick={() => incrementDueDate(values, setFieldValue)}
                          startIcon={<CalendarIcon />}
                          disabled={loading}
                        >
                          {i18n.t("companies.buttons.addDueDate")}
                        </Button>

                        <Button
                          variant="contained"
                          className={`${classes.actionButton} ${classes.primaryButton}`}
                          onClick={() => handleOpenModalUsers()}
                          startIcon={<PersonIcon />}
                          disabled={loading}
                        >
                          {i18n.t("companies.buttons.user")}
                        </Button>
                      </>
                    ) : null}

                    <Button
                      type="submit"
                      variant="contained"
                      className={`${classes.actionButton} ${classes.primaryButton}`}
                      startIcon={<SaveIcon />}
                      disabled={loading}
                    >
                      {loading
                        ? i18n.t("companies.buttons.saving")
                        : i18n.t("companies.buttons.save")}
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </CardContent>
      </Card>
    </>
  );
}

export function CompaniesManagerGridImproved(props) {
  const { records, onSelect, viewMode = "table" } = props;
  const classes = useStyles();
  const { dateToClient } = useDate();
  const { formatDateTime } = useTimezone();

  const renderStatus = (row) => {
    return row.status === false ? i18n.t("companies.values.no") : i18n.t("companies.values.yes");
  };

  const renderPlan = (row) => {
    return row.plan?.name || "-";
  };

  const renderRecurrence = (recurrence) => {
    if (!recurrence) return "-";

    const recurrenceMap = {
      MENSAL: i18n.t("companies.values.monthly"),
      BIMESTRAL: i18n.t("companies.values.bimonthly"),
      TRIMESTRAL: i18n.t("companies.values.quarterly"),
      SEMESTRAL: i18n.t("companies.values.semiannual"),
      ANUAL: i18n.t("companies.values.annual"),
    };

    return recurrenceMap[recurrence] || recurrence;
  };

  const getRowClass = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return classes.warningRow;
      }
      if (diff <= 0) {
        return classes.dangerRow;
      }
    }
    return "";
  };

  const getStatusChipClass = (status) => {
    return status ? classes.statusActive : classes.statusInactive;
  };

  if (records.length === 0) {
    return (
      <Card className={classes.tableCard}>
        <CardContent className={classes.emptyState}>
          <BusinessIcon style={{ fontSize: 64, color: "#ccc", marginBottom: 16 }} />
          <Typography variant="h6" color="textSecondary">
            {i18n.t("companies.grid.emptyTitle")}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("companies.grid.emptySubtitle")}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.tableCard}>
      {viewMode === "cards" ? (
        <RecordsCardsGrid>
          {records.map((row) => (
            <RecordCard
              key={`company-card-${row.id}`}
              title={row.name || "-"}
              actions={
                <IconButton
                  onClick={() => onSelect(row)}
                  className={classes.editButton}
                  size="small"
                >
                  <EditIcon />
                </IconButton>
              }
            >
              <RecordCardMeta>{row.email || "-"}</RecordCardMeta>
              <RecordCardMeta>{row.phone || "-"}</RecordCardMeta>
              <Box display="flex" flexWrap="wrap" style={{ gap: 6, marginTop: 6 }}>
                <Chip label={renderPlan(row)} className={classes.planChip} size="small" />
                <Chip
                  label={renderStatus(row)}
                  className={`${classes.statusChip} ${getStatusChipClass(row.status)}`}
                  size="small"
                />
              </Box>
              <RecordCardMeta>
                {i18n.t("companies.table.dueDate")}: {dateToClient(row.dueDate) || "—"}
                {row.recurrence ? ` · ${renderRecurrence(row.recurrence)}` : ""}
              </RecordCardMeta>
            </RecordCard>
          ))}
        </RecordsCardsGrid>
      ) : (
      <Table size="small" className={classes.table}>
        <TableHead className={classes.tableHeader}>
          <TableRow>
            <TableCell style={{ width: 48 }} />
            <TableCell>{i18n.t("companies.table.name")}</TableCell>
            <TableCell>{i18n.t("companies.table.email")}</TableCell>
            <TableCell>{i18n.t("companies.table.phone")}</TableCell>
            <TableCell>{i18n.t("companies.table.plan")}</TableCell>
            <TableCell>{i18n.t("companies.table.status")}</TableCell>
            <TableCell>{i18n.t("companies.table.dueDate")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row, key) => (
            <TableRow
              className={`${classes.tableRow} ${getRowClass(row)}`}
              key={row.id || key}
              onClick={() => onSelect(row)}
            >
              <TableCell className={classes.tableCell}>
                <Tooltip title={i18n.t("companies.tooltips.editCompany")}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(row);
                    }}
                    className={classes.editButton}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {row.name || "-"}
                </Typography>
              </TableCell>
              <TableCell className={classes.tableCell}>{row.email || "-"}</TableCell>
              <TableCell className={classes.tableCell}>{row.phone || "-"}</TableCell>
              <TableCell className={classes.tableCell}>
                <Chip label={renderPlan(row)} className={classes.planChip} size="small" />
              </TableCell>
              <TableCell className={classes.tableCell}>
                <Chip
                  label={renderStatus(row)}
                  className={`${classes.statusChip} ${getStatusChipClass(row.status)}`}
                  size="small"
                />
              </TableCell>
              <TableCell className={classes.tableCell}>
                <Typography variant="body2">{dateToClient(row.dueDate)}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {renderRecurrence(row.recurrence)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      )}
    </Card>
  );
}

export default function CompaniesManagerImproved() {
  const classes = useStyles();
  const { list, save, update, remove, find } = useCompanies();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState({ ...EMPTY_COMPANY_RECORD });
  const [openEditDialog, setOpenEditDialog] = useState(false);

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const companyList = await list();
      setRecords(companyList);
    } catch (e) {
      toast.error(i18n.t("companies.messages.loadError"));
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        status: toBoolFlag(data.status, true),
        campaignsEnabled: toBoolFlag(data.campaignsEnabled, false),
        planId: data.planId ? Number(data.planId) : data.planId,
      };
      let savedCompany;
      if (payload.id !== undefined) {
        savedCompany = await update(payload);
      } else {
        savedCompany = await save(payload);
      }

      await loadPlans();

      const companyId = savedCompany?.id || payload.id;
      if (companyId) {
        try {
          const full = await find(companyId);
          await handleSelect(full);
        } catch (err) {
          console.warn("[CompaniesManager] find tras guardar falló, usando respuesta del PUT", err);
          if (savedCompany && savedCompany.id) {
            await handleSelect(savedCompany);
          }
        }
      }

      if (!data.id) {
        handleCancel();
      }

      toast.success(i18n.t("companies.messages.saveSuccess"));
    } catch (e) {
      toast.error(i18n.t("companies.messages.saveError"));
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadPlans();
      handleCloseEditModal();
      toast.success(i18n.t("companies.messages.deleteSuccess"));
    } catch (e) {
      toast.error(i18n.t("companies.messages.deleteError"));
    }
    setLoading(false);
  };

  const handleOpenDeleteDialog = () => {
    setShowConfirmDialog(true);
  };

  /** Cierra el modal de edición y limpia la empresa seleccionada */
  const handleCloseEditModal = () => {
    setOpenEditDialog(false);
    setRecord({ ...EMPTY_COMPANY_RECORD });
  };

  /** Limpiar campos del formulario (botón Limpiar), sin cerrar el modal */
  const handleCancel = () => {
    setRecord((prev) => ({
      ...prev,
      name: "",
      email: "",
      phone: "",
      planId: "",
      status: "true",
      campaignsEnabled: "false",
      dueDate: "",
      recurrence: "",
    }));
  };

  const handleSelect = async (data) => {
    setOpenRegisterDialog(false);

    let campaignsEnabled = false;

    const settingsList = Array.isArray(data.settings) ? data.settings : [];
    const setting = settingsList.find((s) => s.key.indexOf("campaignsEnabled") > -1);
    if (setting) {
      campaignsEnabled = setting.value === "true" || setting.value === "enabled";
    }

    // ✅ FORMATEAR FECHA DE VENCIMIENTO CORRECTAMENTE
    let formattedDueDate = "";
    if (data.dueDate) {
      const momentDate = moment(data.dueDate);
      if (momentDate.isValid()) {
        formattedDueDate = momentDate.format("YYYY-MM-DD");
      }
    }

    // ✅ DEBUG: Verificar qué datos vienen de la empresa
    console.log(`[handleSelect] Datos recibidos para empresa ${data.id}:`, {
      id: data.id,
      name: data.name,
      timezone: data.timezone,
      hasSettings: !!data.settings,
      settingsCount: data.settings?.length || 0,
    });

    // ✅ CARGAR TIMEZONE DESDE SETTINGS
    // ✅ PRIORIDAD: Settings.timezone > Companies.timezone > Default
    let timezone = "America/Lima"; // Default

    // ✅ PRIMERO: Intentar usar timezone de Companies si está disponible
    if (data.timezone) {
      timezone = data.timezone;
      console.log(`[handleSelect] Timezone inicial desde Companies: ${data.timezone}`);
    }

    // ✅ SEGUNDO: Intentar cargar desde Settings (sobrescribe si existe)
    if (data.id) {
      try {
        const { data: timezoneData } = await api.get(`/settings/timezone`, {
          params: { companyId: data.id },
        });
        if (timezoneData && timezoneData.value) {
          timezone = timezoneData.value;
          console.log(`[handleSelect] Timezone cargado desde Settings: ${timezoneData.value}`);
        } else {
          // Settings no tiene valor, mantener el de Companies si existe
          if (data.timezone) {
            console.log(
              `[handleSelect] Settings vacío, usando timezone de Companies: ${data.timezone}`
            );
          }
        }
      } catch (error) {
        // ✅ 404 es esperado si Settings no tiene timezone configurado aún
        // ✅ NO mostrar warning para 404, solo para otros errores
        if (error.response?.status === 404) {
          // 404 esperado, usar timezone de Companies si existe
          if (data.timezone) {
            console.log(
              `[handleSelect] Settings no configurado (404), usando timezone de Companies: ${data.timezone}`
            );
          } else {
            console.log(
              `[handleSelect] Settings no configurado (404) y Companies sin timezone, usando default: America/Lima`
            );
          }
        } else {
          // Error diferente a 404, mostrar warning
          console.warn(`[handleSelect] Error cargando timezone desde Settings (no es 404):`, error);
          // Mantener timezone de Companies si existe
          if (!data.timezone) {
            console.warn(`[handleSelect] No hay timezone en Companies ni Settings, usando default`);
          }
        }
      }
    } else if (data.timezone) {
      // ✅ Si es nueva empresa pero tiene timezone en data, usarlo
      timezone = data.timezone;
      console.log(`[handleSelect] Nueva empresa con timezone: ${data.timezone}`);
    }

    console.log(`[handleSelect] Timezone final seleccionado: ${timezone}`);

    const newRecord = {
      id: data.id,
      name: data.name || "",
      phone: data.phone || "",
      email: data.email || "",
      planId: data.planId ? String(data.planId) : "",
      status: toFlagString(data.status, true),
      campaignsEnabled: toFlagString(campaignsEnabled, false),
      dueDate: formattedDueDate,
      recurrence: data.recurrence || "",
      timezone: timezone, // ✅ Agregar timezone al record
    };

    // ✅ Asegurar que planId sea string también en el record actualizado
    if (newRecord.planId && typeof newRecord.planId !== "string") {
      newRecord.planId = String(newRecord.planId);
    }

    setRecord((prev) => ({
      ...prev,
      ...newRecord,
    }));
    setOpenEditDialog(true);
  };

  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
  const [viewMode, setViewMode] = useViewMode("companies", "cards");

  const handleOpenRegisterDialog = () => {
    handleCloseEditModal();
    setOpenRegisterDialog(true);
  };

  const handleCloseRegisterDialog = () => {
    setOpenRegisterDialog(false);
  };

  const handleRegisterSubmit = async (companyData) => {
    // ✅ MEJORADO: El callback ya recibe la empresa creada del backend
    // Solo necesitamos actualizar la lista y cerrar el modal
    try {
      // ✅ Actualizar la lista de empresas para mostrar la nueva
      await loadPlans();
      // ✅ Cerrar el modal
      handleCloseRegisterDialog();
      // ✅ El mensaje de éxito ya se mostró en RegisterCompanyImproved
      // No duplicar el mensaje, solo confirmar
      toast.success(i18n.t("companies.messages.saveSuccess"));
    } catch (e) {
      console.error("Error actualizando lista de empresas:", e);
      // ✅ Aún así cerrar el modal y mostrar mensaje de éxito
      handleCloseRegisterDialog();
      toast.success(i18n.t("companies.messages.registerRefreshPage"));
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={openEditDialog && !!record.id}
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ className: classes.modalPaper }}
      >
        <DialogContent className={classes.modalContent}>
          {record.id ? (
            <CompanyFormImproved
              initialValue={record}
              onDelete={handleOpenDeleteDialog}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              loading={loading}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" style={{ gap: 8 }}>
        <Typography variant="h5" style={{ fontWeight: "bold" }}>
          {i18n.t("companies.title")}
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenRegisterDialog}
            className={classes.primaryButton}
          >
            {i18n.t("companies.buttons.registerCompany")}
          </Button>
        </Box>
      </Box>

      <CompaniesManagerGridImproved records={records} onSelect={handleSelect} viewMode={viewMode} />

      <Dialog
        open={openRegisterDialog}
        onClose={handleCloseRegisterDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogContent
          style={{
            padding: 0,
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <RegisterCompanyImproved
            onSuccess={handleRegisterSubmit}
            onCancel={handleCloseRegisterDialog}
          />
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        title={i18n.t("companies.messages.deleteTitle")}
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        {i18n.t("companies.messages.deleteMessage")}
      </ConfirmationModal>
    </div>
  );
}
